import { getItemTitle, getItemDeepLink, checkCurrentUserAppAccess } from '../../../itemRef.js';

export const discussedItemsMixin = {

    //********************* GET DISCUSSED ITEM ENTRIES ********************************//
    // The item-discussion cards to render inline in the chat timeline for the
    // active dm/channel (see gh-users-side-bar.getDiscussedItems). Returns []
    // with no extra fetches if there are none.
    // * returns Array<{ conversationId, itemRef, itemTitle, createdAt, participants, replyCount }>
    async getDiscussedItemEntries() {
        const items = this.getSidebar()?.getDiscussedItems(this.getParticipantIds()) || [];

        if(!items.length) return [];

        return Promise.all(items.map(item => this.buildDiscussedItemEntry(item)));
    },

    //********************* BUILD DISCUSSED ITEM ENTRY ********************************//
    // Fetches the conversation/title data needed to render a single
    // item-discussion card (see getDiscussedItemEntries/maybeInsertItemDiscussionCard).
    // * item (Object) — { conversation_id, item_ref, participants, ... }
    // * returns Object — { conversationId, itemRef, itemTitle, createdAt, participants, replyCount }
    async buildDiscussedItemEntry(item) {
        const { app_id, item_id } = item.item_ref;

        const [conversation, hasAccess] = await Promise.all([
            this.fetchConversation(item.conversation_id),
            checkCurrentUserAppAccess(app_id)
        ]);

        const itemTitle = hasAccess ? await getItemTitle(app_id, item_id) : 'Unavailable';

        await this.prefetchParticipants(conversation.participants);

        return {
            conversationId: item.conversation_id,
            itemRef: item.item_ref,
            itemTitle,
            hasAccess,
            createdAt: conversation.created_at || Date.now(),
            participants: conversation.participants.map(String),
            replyCount: conversation.messages.length
        };
    },

    //********************* RENDER ITEM DISCUSSION CARDS ********************************//
    // Fetches the item-discussions tied to this dm/channel (see
    // getDiscussedItemEntries) and inserts a card for each into the chat
    // timeline, positioned chronologically by createdAt (see
    // insertItemDiscussionCard). Fire-and-forget from renderActiveConversation -
    // doesn't block the initial message render.
    async renderItemDiscussionCards() {
        const conversationId = this.conversationId;
        const entries = await this.getDiscussedItemEntries();

        if(!entries.length) return;
        if(this.conversationId !== conversationId) return;

        const chat = this.querySelector('.chat');
        if(!chat) return;

        for(const entry of entries) {
            this.insertItemDiscussionCard(chat, entry);
        }

        this.scrollChatToBottom();
    },

    //********************* INSERT ITEM DISCUSSION CARD ********************************//
    // Inserts a single item-discussion card into the chat timeline at the
    // correct chronological position, comparing entry.createdAt against the
    // data-timestamp of already-rendered messages/cards.
    // * chat (HTMLElement) — the .chat container
    // * entry (Object) — see getDiscussedItemEntries
    insertItemDiscussionCard(chat, entry) {
        const cardHtml = this.renderItemDiscussionCardHtml(entry);

        const nextElement = [...chat.querySelectorAll('[data-timestamp]')]
            .find(el => Number(el.dataset.timestamp) > entry.createdAt);

        if(nextElement) {
            nextElement.insertAdjacentHTML('beforebegin', cardHtml);
        } else {
            chat.insertAdjacentHTML('beforeend', cardHtml);
        }

        const card = nextElement ? nextElement.previousElementSibling : chat.lastElementChild;
        if(entry.hasAccess !== false) {
            card.onclick = () => this.openDiscussedItem(entry.conversationId, entry.itemRef);
        }
    },

    //********************* MAYBE INSERT ITEM DISCUSSION CARD ********************************//
    // Called by gh-users-side-bar.addDiscussedItem whenever an item-thread is
    // created/joined - either pushed live to other participants via
    // handleEmitToUser, or echoed back to the active user via
    // item_thread_discussed. If that item-thread matches the currently open
    // dm/channel and isn't already shown, fetches its card data and inserts it
    // live, so newly-discussed items appear without a reload.
    // * item (Object) — { conversation_id, item_ref, participants, ... }
    async maybeInsertItemDiscussionCard(item) {
        if(this.viewMode !== 'dm' && this.viewMode !== 'channel') return;
        if(!this.getSidebar()?.itemMatchesParticipants(item, this.getParticipantIds())) return;
        if(this.querySelector(`.item_discussion_card[data-conversation-id="${item.conversation_id}"]`)) return;

        const conversationId = this.conversationId;
        const entry = await this.buildDiscussedItemEntry(item);

        if(this.conversationId !== conversationId) return;

        const chat = this.querySelector('.chat');
        if(!chat) return;

        this.insertItemDiscussionCard(chat, entry);
        this.scrollChatToBottom();
    },

    //********************* INCREMENT ITEM DISCUSSION REPLY COUNT ********************************//
    // Called by gh-users-side-bar.handleEmitToUser when a message is pushed to
    // an item-thread that has an inline card in the currently open dm/channel
    // (see maybeInsertItemDiscussionCard). Bumps that card's "N replies" link
    // in place, without re-fetching the conversation.
    // * conversationId (string) — conversation_id of the item-thread
    incrementItemDiscussionReplyCount(conversationId) {
        const card = this.querySelector(`.item_discussion_card[data-conversation-id="${conversationId}"]`);
        if(!card) return;

        const link = card.querySelector('.message__thread_link');
        if(!link) return;

        const replyCount = Number(card.dataset.replyCount || 0) + 1;
        card.dataset.replyCount = replyCount;
        link.textContent = `${replyCount} repl${replyCount === 1 ? 'y' : 'ies'}`;
    },

    //********************* RENDER ITEM DISCUSSION CARD HTML ********************************//
    // Markup for one inline item-discussion card (see insertItemDiscussionCard).
    // * entry (Object) — see getDiscussedItemEntries
    renderItemDiscussionCardHtml(entry) {
        const { app_id, item_id, view_id } = entry.itemRef;
        const disabledClass = entry.hasAccess === false ? ' item_discussion_card--disabled' : '';

        return /*html*/`
            <div class="item_discussion_card${disabledClass}" data-timestamp="${entry.createdAt}" data-conversation-id="${entry.conversationId}" data-app-id="${app_id}" data-item-id="${item_id}" data-view-id="${view_id || ''}" data-reply-count="${entry.replyCount}">
                <div class="item_discussion_card__avatars">
                    ${
                        entry.participants.slice(0, 4).map(userId => {
                            const user = this.resolveUser(userId);
                            const avatarUrl = user?.avatar_128 || user?.avatar_512;
                            return `<gh-avatar-webcomponent name="${user?.fullname || 'Not Found'}"${avatarUrl ? ` url="${avatarUrl}"` : ''}></gh-avatar-webcomponent>`;
                        }).join('')
                    }
                    <span class="item_discussion_card__avatar_count">${entry.participants.length}</span>
                </div>
                <div class="item_discussion_card__body">
                    <div class="item_discussion_card__header">
                        <span class="item_discussion_card__title">${entry.itemTitle}</span>
                        <span class="item_discussion_card__time">
                            ${new Date(entry.createdAt).toLocaleTimeString(navigator.language, {
                                hour: "2-digit",
                                minute: "2-digit",
                            })}
                        </span>
                    </div>
                    <button type="button" class="message__thread_link">${entry.replyCount} repl${entry.replyCount === 1 ? 'y' : 'ies'}</button>
                </div>
                ${entry.hasAccess !== false ? `<svg class="item_discussion_card__arrow" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"><path d="M9 6l6 6-6 6"/></svg>` : ''}
            </div>`;
    },
};
